import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  Users,
  MessageCircle,
  User,
  UserCheck,
  LogOut,
  X,
  Settings,
  ChevronDown,
  ChevronRight,
  Key,
  Logs,
  BarChart3,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([
    'configuration',
  ]);

  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev =>
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <LayoutDashboard className='w-5 h-5' />,
      path: '/admin/dashboard',
    },
    {
      id: 'users',
      label: 'User Management',
      icon: <Users className='w-5 h-5' />,
      path: '/admin/users',
    },
    {
      id: 'chats',
      label: 'Chat History',
      icon: <MessageCircle className='w-5 h-5' />,
      path: '/admin/chats',
    },
    {
      id: 'ads',
      label: 'Link Management',
      icon: <BarChart3 className='w-5 h-5' />,
      path: '/admin/ads',
    },
    {
      id: 'analytics',
      label: 'Traffic Insights',
      icon: <TrendingUp className='w-5 h-5' />,
      path: '/admin/analytics',
    },

    // Only show Configuration menu for super-admin
    ...(user?.role === 'super-admin'
      ? [
          {
            id: 'configuration',
            label: 'Settings',
            icon: <Settings className='w-5 h-5' />,
            hasSubMenu: true,
            subItems: [
              {
                id: 'local-users',
                label: 'Team Setup',
                icon: <UserCheck className='w-4 h-4' />,
                path: '/admin/local-users',
              },
              {
                id: 'webhook-api-keys',
                label: 'Webhook API Keys',
                icon: <Key className='w-4 h-4' />,
                path: '/admin/configuration/webhook-api-keys',
              },
              {
                id: 'webhook-calls',
                label: 'Webhook Calls',
                icon: <Logs className='w-4 h-4' />,
                path: '/admin/configuration/webhook-calls',
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <div
      className={`
      fixed left-0 top-0 w-60 bg-white border-r border-gray-200 flex flex-col h-screen z-50
      transform transition-transform duration-300 ease-in-out
      ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      lg:translate-x-0
    `}
    >
      {/* Mobile close button */}
      <div className='lg:hidden absolute top-4 right-4 z-10'>
        <Button
          variant='ghost'
          size='sm'
          onClick={onClose}
          className='h-8 w-8 p-0'
        >
          <X className='h-4 w-4' />
        </Button>
      </div>

      <div className='p-4 text-center border-b border-gray-200'>
        <h1 className='text-xl font-bold text-blue-600'>PartnerGPT</h1>
        <p className='text-sm text-gray-500'>Admin Panel</p>
      </div>

      {user && (
        <Link
          to='/profile'
          className='p-4 border-b border-gray-200 flex items-center gap-3 hover:bg-gray-50 transition-colors cursor-pointer group'
          onClick={() => onClose()}
          title='Go to Profile'
        >
          <div className='w-8 h-8 bg-gradient-to-br from-primary to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 group-hover:shadow-md transition-shadow'>
            <User className='w-4 h-4 text-white' />
          </div>
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-medium text-gray-900 truncate group-hover:text-primary transition-colors'>
              {user.firstName || ''} {user.lastName || ''}
            </p>
            <div className='flex items-center gap-1 mt-1'>
              <span className='px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full'>
                {user.role}
              </span>
              <span className='text-xs text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity'>
                • View Profile
              </span>
            </div>
          </div>
        </Link>
      )}

      <nav className='flex-1 p-4 overflow-y-auto'>
        <div className='space-y-2'>
          {menuItems.map(item => (
            <div key={item.id}>
              {item.hasSubMenu ? (
                <>
                  {/* Main menu item with toggle */}
                  <button
                    onClick={() => toggleMenu(item.id)}
                    className={`
                      w-full justify-between gap-3 text-sm flex items-center px-3 py-2 rounded-md font-medium transition-colors
                      ${
                        location.pathname.startsWith('/admin/configuration') ||
                        location.pathname.startsWith('/admin/local-users')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }
                    `}
                  >
                    <div className='flex items-center gap-3'>
                      {item.icon}
                      <span className='truncate'>{item.label}</span>
                    </div>
                    {expandedMenus.includes(item.id) ? (
                      <ChevronDown className='w-4 h-4' />
                    ) : (
                      <ChevronRight className='w-4 h-4' />
                    )}
                  </button>

                  {/* Sub-menu items */}
                  {expandedMenus.includes(item.id) && item.subItems && (
                    <div className='ml-6 mt-1 space-y-1'>
                      {item.subItems.map(subItem => (
                        <Link
                          key={subItem.id}
                          to={subItem.path}
                          onClick={() => onClose()}
                          className={`
                            w-full justify-start gap-3 text-sm flex items-center px-3 py-2 rounded-md font-medium transition-colors
                            ${
                              location.pathname === subItem.path
                                ? 'bg-primary/10 text-primary border-l-2 border-primary'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }
                          `}
                        >
                          {subItem.icon}
                          <span className='truncate'>{subItem.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                /* Regular menu item */
                <Link
                  to={item.path!}
                  onClick={() => onClose()}
                  className={`
                    w-full justify-start gap-3 text-sm flex items-center px-3 py-2 rounded-md font-medium transition-colors
                    ${
                      location.pathname === item.path
                        ? 'bg-primary text-primary-foreground'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }
                  `}
                >
                  {item.icon}
                  <span className='truncate'>{item.label}</span>
                </Link>
              )}
            </div>
          ))}
        </div>
      </nav>

      <div className='p-4 border-t border-gray-200'>
        <Button
          variant='ghost'
          className='w-full justify-start gap-3 text-red-600 hover:text-red-700 hover:bg-red-50 text-sm'
          onClick={logout}
        >
          <LogOut className='w-5 h-5' />
          <span className='truncate'>Logout</span>
        </Button>
      </div>
    </div>
  );
};

export default AdminSidebar;
